import React, { useState, useEffect } from 'react';
import { videoService } from '../services/videoService';

export default function PublicVideoPlayer({ publicUrl, className = "w-full h-full" }) {
  const [videoError, setVideoError] = useState(false);
  const [videoLoading, setVideoLoading] = useState(true);
  const [videoUrl, setVideoUrl] = useState(null);
  const [urlLoading, setUrlLoading] = useState(false);
  const [urlError, setUrlError] = useState(false);

  // Fetch video URL when publicUrl changes
  useEffect(() => {
    if (!publicUrl) {
      setVideoUrl(null);
      setUrlLoading(false);
      setUrlError(false);
      return;
    }

    const fetchVideoUrl = async () => {
      setUrlLoading(true);
      setUrlError(false);
      setVideoError(false);
      
      try {
        const url = await videoService.getPublicVideoStreamUrl(publicUrl);
        setVideoUrl(url);
      } catch (error) {
        console.error('Error fetching public video URL for publicUrl:', publicUrl, 'Error:', error);
        setUrlError(true);
      } finally {
        setUrlLoading(false);
      }
    };

    fetchVideoUrl();
  }, [publicUrl]);

  const handleError = (event) => {
    console.error('Public video error for publicUrl:', publicUrl, 'URL:', videoUrl, 'Error:', event);
    setVideoError(true);
    setVideoLoading(false);
  };

  const handleLoadStart = () => {
    setVideoLoading(true);
    setVideoError(false);
  };

  const handleLoadedData = () => {
    setVideoLoading(false);
  };

  const handleRetry = () => {
    if (publicUrl) {
      const fetchVideoUrl = async () => {
        setUrlLoading(true);
        setUrlError(false);
        setVideoError(false);
        
        try {
          const url = await videoService.getPublicVideoStreamUrl(publicUrl);
          setVideoUrl(url);
        } catch (error) {
          console.error('Error retrying public video URL for publicUrl:', publicUrl, 'Error:', error);
          setUrlError(true);
        } finally {
          setUrlLoading(false);
        }
      };

      fetchVideoUrl();
    }
  };

  // Show error state for URL fetching errors
  if (urlError) {
    return (
      <div className={`${className} flex items-center justify-center bg-secondary text-muted-foreground`}>
        <div className="text-center">
          <div className="text-xl mb-2">⚠️</div>
          <p className="text-sm mb-2">Failed to load video</p>
          <button 
            onClick={handleRetry}
            className="text-xs text-primary hover:text-primary/80 underline"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Show error state for video playback errors
  if (videoError) {
    return (
      <div className={`${className} flex items-center justify-center bg-secondary text-muted-foreground`}>
        <div className="text-center">
          <div className="text-xl mb-2">⚠️</div>
          <p className="text-sm">Video unavailable</p>
        </div>
      </div>
    );
  }

  // Show placeholder when no publicUrl provided
  if (!publicUrl) {
    return (
      <div className={`${className} flex items-center justify-center bg-secondary text-muted-foreground`}>
        <div className="text-center">
          <div className="text-xl mb-2">📹</div>
          <p className="text-sm">No video</p>
        </div>
      </div>
    );
  }

  // Show loading state while fetching URL
  if (urlLoading || !videoUrl) {
    return (
      <div className={`${className} flex items-center justify-center bg-black`}>
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mb-2 mx-auto"></div>
          {/* <p className="text-sm">Loading video...</p> */}
        </div>
      </div>
    );
  }

  return (
    <div className={`${className} relative bg-black`}>
      {videoLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black z-10 pointer-events-auto">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
        </div>
      )}
      
      <video
        className={`w-full h-full object-contain ${videoLoading ? 'pointer-events-none' : ''}`}
        controls
        preload="metadata"
        onError={handleError}
        onLoadStart={handleLoadStart}
        onLoadedData={handleLoadedData}
        src={videoUrl}
        crossOrigin="anonymous"
      >
        Your browser does not support the video tag.
      </video>
    </div>
  );
} 